import { NavLink } from "react-router";
import { formatCents } from "@budgeter/shared";
import { useSnapshot } from "../data/StoreContext";
import { accountsWithBalances, type AccountWithBalance } from "../data/selectors";
import { cn, Logo } from "../ui";
import { fullNav, settingsNav } from "./navItems";
import { SyncIndicator } from "./SyncIndicator";
import { usePendingImports } from "./usePendingImports";

const navClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "flex items-center gap-2.5 rounded-lg px-3 py-[9px] text-[14.5px] font-semibold text-ink no-underline",
    isActive && "bg-surface shadow-soft",
  );

function AccountGroup({
  title,
  accounts,
  showTotal,
}: {
  title: string;
  accounts: AccountWithBalance[];
  showTotal: boolean;
}) {
  if (accounts.length === 0) {
    return null;
  }
  const total = accounts.reduce((s, a) => s + a.balance.working, 0);
  return (
    <div>
      <div className="flex justify-between px-3 pb-1.5 text-[12px] font-bold tracking-[.6px] text-muted uppercase">
        <span>{title}</span>
        {showTotal && <span className="num">{formatCents(total)}</span>}
      </div>
      {accounts.map((a) => (
        <NavLink
          key={a.id}
          to={`/accounts/${a.id}`}
          className={({ isActive }) =>
            cn(
              "flex justify-between gap-2 rounded-lg px-3 py-1.5 text-[13.5px] text-ink no-underline hover:bg-surface/60",
              isActive && "bg-surface shadow-soft",
            )
          }
        >
          <span className="truncate">{a.name}</span>
          <span
            className={cn(
              "num",
              a.balance.working < 0 && a.onBudget && "text-neg",
              !a.onBudget && "text-muted",
            )}
          >
            {formatCents(a.balance.working)}
          </span>
        </NavLink>
      ))}
    </div>
  );
}

export function Sidebar() {
  const s = useSnapshot();
  const { onBudget, offBudget } = accountsWithBalances(s);
  const pending = usePendingImports();
  return (
    <nav
      aria-label="Main"
      className="sticky top-0 flex h-dvh w-[260px] shrink-0 flex-col gap-[22px] overflow-y-auto bg-chip px-3.5 py-5"
    >
      <div className="flex items-center gap-2.5 px-2">
        <Logo />
        <span className="text-[19px] font-extrabold tracking-[-0.2px]">Budgeter</span>
      </div>
      <div className="flex flex-col gap-0.5">
        {fullNav.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={navClass}>
            <Icon size={18} strokeWidth={2} aria-hidden />
            {label}
            {to === "/import" && pending > 0 && (
              <span className="ml-auto rounded-full bg-accent px-2 py-px text-[12px] text-white">
                {pending}
              </span>
            )}
          </NavLink>
        ))}
      </div>
      <AccountGroup title="On budget" accounts={onBudget} showTotal />
      <AccountGroup title="Off budget" accounts={offBudget} showTotal={false} />
      <NavLink to="/accounts/all" className={navClass}>
        All accounts
      </NavLink>
      <div className="mt-auto flex flex-col gap-1">
        <NavLink to={settingsNav.to} className={navClass}>
          <settingsNav.icon size={18} strokeWidth={2} aria-hidden />
          {settingsNav.label}
        </NavLink>
        <div className="flex items-center gap-2 px-3 py-2">
          <SyncIndicator />
          <kbd className="ml-auto rounded-md border border-[#D9CCBA] px-1.5 py-px font-sans text-[12px] text-muted">
            ⌘K
          </kbd>
        </div>
      </div>
    </nav>
  );
}
